import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import styled from '@emotion/styled';
import { SignInPage, SignInCard, SignInWordmark, SignInTagline, AlertBanner } from '../styled-components';
import { WaveformBackground, CopyButton } from '../components';

type ProbeState =
  | { kind: 'pending' }
  | { kind: 'delivered' };

const LoginCodeText = styled.span({
  flex: 1,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 13,
  color: 'var(--text-secondary)',
  wordBreak: 'break-all',
  '&::selection': {
    color: 'var(--text-primary)',
    background: 'var(--accent-cyan-glow)',
  },
});

export function CliCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const redirectUri = searchParams.get('redirect_uri');

  const [probe, setProbe] = useState<ProbeState>({ kind: 'pending' });

  useEffect(() => {
    if (!code || !state || !redirectUri) return;
    let cancelled = false;

    const target = new URL(redirectUri);
    target.searchParams.set('code', code);
    target.searchParams.set('state', state);

    // no-cors so the browser sends the request to the CLI's localhost
    // listener regardless of CORS headers; we only care that the connection
    // succeeded (CLI received the code) vs. failed (e.g. SSH, CLI not running).
    fetch(target.toString(), { mode: 'no-cors' })
      .then(() => { if (!cancelled) setProbe({ kind: 'delivered' }); })
      .catch(() => { /* leave as pending so the manual code is shown */ });

    return () => { cancelled = true; };
  }, [code, state, redirectUri]);

  const noParams = !code || !state || !redirectUri;

  useEffect(() => {
    if (!noParams) return;
    navigate('/', { replace: true });
  }, [noParams, navigate]);

  // Compact `code.state` token — both halves are base64url so they never
  // contain `.`. The CLI's parse_callback_input recognises this shape.
  const loginToken = code && state ? `${code}.${state}` : null;

  return (
    <SignInPage>
      <WaveformBackground />
      <SignInWordmark>
        <img src='/static/guava-logo.png' alt='Guava' style={{ height: 400, filter: 'grayscale(1) brightness(0.75)', margin: '-80px 0' }} />
      </SignInWordmark>

      {probe.kind === 'delivered' && <AlertBanner variant='green' style={{ textAlign: 'center', fontSize: 14, maxWidth: 420 }}>

        <strong>Login successful</strong>
        <div style={{ marginTop: 4, fontSize: 13, opacity: 0.85 }}>
          Return to your terminal — the Guava CLI is now signed in and ready to use.
        </div>

      </AlertBanner>}

      <SignInCard style={{ gap: 14 }}>
        {loginToken && (
          <>
            {probe.kind !== 'delivered' && (
              <div style={{ fontSize: 16, color: 'var(--text-primary)', textAlign: 'center', paddingBottom: '16px' }}>
                <strong>Almost there</strong>
                <div style={{ marginTop: 4, fontSize: 13, opacity: 0.85 }}>
                  Copy the code below and paste it into the <strong>"Login code"</strong> prompt in your terminal.
                </div>
              </div>
            )}
            {probe.kind === 'delivered' && (
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center' }}>
                If your terminal doesn't show a success message, copy this code and paste it into the <strong>"Login code"</strong> prompt.
              </div>
            )}
            <div style={{
              background: 'var(--surface-2, rgba(255,255,255,0.05))',
              border: '1px solid var(--border, rgba(255,255,255,0.1))',
              borderRadius: 6,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
            }}>
              <LoginCodeText>{loginToken}</LoginCodeText>
              <CopyButton text={loginToken} color='var(--text-primary)' size={28} />
            </div>
          </>
        )}
      </SignInCard>

      <button
        type='button'
        onClick={() => navigate('/', { replace: true })}
        style={{
          marginTop: 16,
          background: 'none',
          border: 'none',
          padding: 0,
          color: 'var(--text-secondary)',
          fontSize: 13,
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        Go to dashboard
      </button>
    </SignInPage>
  );
}
